/**
 * Phase 19 — Enterprise Mock Payment Service Layer
 * In-memory relational store with 510+ realistic disbursement records, readiness validation,
 * partial settlements, duplicate protection, failure diagnostics, and SAP F110 simulator.
 */

import {
  PaymentRecord,
  PaymentMetricsSummary,
  PaymentFilterState,
  CreatePaymentInput,
  PaymentException,
} from '@/types/domain/payment-management';
import { MOCK_INVOICES, MOCK_PURCHASE_ORDERS } from '@/mocks/database';
import { mockInvoiceService } from '@/services/mock/MockInvoiceService';
import { mockPurchaseOrderService } from '@/services/mock/MockPurchaseOrderService';
import { env } from '@/config/env';
import { notifyGlobal } from '@/context/NotificationContext';
import {
  roundCurrency,
  calculateRemainingBalance,
  calculateDaysOverdue,
  determinePaymentType,
  validatePaymentAmount,
} from '@/features/payments/utils/paymentCalculations';
import { detectDuplicatePayment } from '@/features/payments/utils/duplicatePaymentProtection';
import { evaluatePaymentEligibility } from '@/features/payments/utils/paymentReadiness';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const GENERATED_PAYMENT_RECORDS: PaymentRecord[] = Array.from({ length: 510 }, (_, i) => {
  const payNum = 8001 + i;
  const id = `pay-${payNum}`;
  const paymentReference = `PAY-${payNum}`;

  const inv = MOCK_INVOICES[i % MOCK_INVOICES.length]!;
  const po = MOCK_PURCHASE_ORDERS.find((p) => p.id === inv.poId || p.poNumber === inv.poNumber) || MOCK_PURCHASE_ORDERS[0]!;

  const isFirst = i === 0 || paymentReference === 'PAY-8001';
  const isSecond = i === 1 || paymentReference === 'PAY-8002';
  const isThird = i === 2 || paymentReference === 'PAY-8003';
  const isFourth = i === 3 || paymentReference === 'PAY-8004';
  const isFifth = i === 4 || paymentReference === 'PAY-8005';
  const isSixth = i === 5 || paymentReference === 'PAY-8006';
  const isSeventh = i === 6 || paymentReference === 'PAY-8007';
  const isEighth = i === 7 || paymentReference === 'PAY-8008';

  const invoicePayableAmount = roundCurrency(inv.totalAmount);
  let amount = invoicePayableAmount;
  let paidAmount = invoicePayableAmount;
  let remainingAmount = 0;
  let status: PaymentRecord['status'] = 'PAID';
  let paymentType: PaymentRecord['paymentType'] = 'FULL';
  let reconciliationStatus: PaymentRecord['reconciliationStatus'] = 'MATCHED';
  let failureCode: string | undefined;
  let failureReason: string | undefined;
  let retryCount = 0;

  if (isFirst) {
    // Clean Full Payment
    status = 'PAID';
    amount = invoicePayableAmount;
    paidAmount = invoicePayableAmount;
    remainingAmount = 0;
    paymentType = 'FULL';
    reconciliationStatus = 'MATCHED';
  } else if (isSecond) {
    // Partial Payment: Paid 40%, 60% remaining
    amount = roundCurrency(invoicePayableAmount * 0.4);
    paidAmount = amount;
    remainingAmount = roundCurrency(invoicePayableAmount - amount);
    status = 'PARTIALLY_PAID';
    paymentType = 'PARTIAL';
    reconciliationStatus = 'MATCHED';
  } else if (isThird) {
    // Scheduled
    status = 'SCHEDULED';
    amount = invoicePayableAmount;
    paidAmount = 0;
    remainingAmount = invoicePayableAmount;
    paymentType = 'FULL';
    reconciliationStatus = 'MISSING_EXTERNAL_RECORD';
  } else if (isFourth) {
    // Processing
    status = 'PROCESSING';
    amount = invoicePayableAmount;
    paidAmount = 0;
    remainingAmount = invoicePayableAmount;
    paymentType = 'FULL';
    reconciliationStatus = 'MISSING_EXTERNAL_RECORD';
  } else if (isFifth) {
    // Failed with Diagnostic
    status = 'FAILED';
    amount = invoicePayableAmount;
    paidAmount = 0;
    remainingAmount = invoicePayableAmount;
    failureCode = 'BANK_UNAVAILABLE';
    failureReason = 'Beneficiary RTGS clearing endpoint timed out after 30,000ms.';
    retryCount = 1;
    reconciliationStatus = 'RECONCILIATION_REQUIRED';
  } else if (isSixth) {
    // Overdue Payment
    status = 'READY_FOR_PAYMENT';
    amount = invoicePayableAmount;
    paidAmount = 0;
    remainingAmount = invoicePayableAmount;
    paymentType = 'FULL';
    reconciliationStatus = 'MISSING_EXTERNAL_RECORD';
  } else if (isSeventh) {
    // Reconciliation Mismatch
    status = 'RECONCILIATION_REQUIRED';
    reconciliationStatus = 'MISMATCH';
    amount = invoicePayableAmount;
    paidAmount = roundCurrency(invoicePayableAmount * 0.95);
    remainingAmount = roundCurrency(invoicePayableAmount * 0.05);
  } else if (isEighth) {
    // On Hold
    status = 'ON_HOLD';
    amount = invoicePayableAmount;
    paidAmount = 0;
    remainingAmount = invoicePayableAmount;
    reconciliationStatus = 'MISSING_EXTERNAL_RECORD';
  } else if (i % 6 === 0) {
    status = 'SCHEDULED';
    paidAmount = 0;
    remainingAmount = invoicePayableAmount;
    reconciliationStatus = 'MISSING_EXTERNAL_RECORD';
  } else if (i % 9 === 0) {
    status = 'FAILED';
    paidAmount = 0;
    remainingAmount = invoicePayableAmount;
    failureCode = 'INSUFFICIENT_FUNDS';
    failureReason = 'Treasury disbursement pool balance insufficient for execution.';
    reconciliationStatus = 'RECONCILIATION_REQUIRED';
  }

  const dueDate = isSixth
    ? new Date(Date.now() - 10 * 86400000).toISOString().slice(0, 10)
    : new Date(Date.now() + ((i % 25) + 5) * 86400000).toISOString().slice(0, 10);

  const { isOverdue, daysOverdue } = calculateDaysOverdue(dueDate, status, remainingAmount);

  const sapSyncStatus: PaymentRecord['sapSyncStatus'] =
    status === 'PAID' ? 'SYNCED' : status === 'FAILED' ? 'SYNC_FAILED' : 'NOT_SYNCED';

  const sapDocumentNumber = status === 'PAID' ? `150000${payNum}` : undefined;

  const paymentDate =
    status === 'PAID' || status === 'PARTIALLY_PAID'
      ? new Date(Date.now() - (i % 15) * 86400000).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);

  const scheduledDate = new Date(Date.now() + (i % 10) * 86400000).toISOString().slice(0, 10);

  const methods: PaymentRecord['paymentMethod'][] = ['NEFT', 'RTGS', 'WIRE_TRANSFER', 'ACH'];
  const paymentMethod = methods[i % methods.length]!;

  const exceptions: PaymentException[] = [];
  if (isFifth) {
    exceptions.push({
      id: `pex-${id}-fail`,
      paymentId: id,
      code: 'PAYMENT_FAILED',
      severity: 'BLOCKING',
      status: 'OPEN',
      description: 'Disbursement failed: Beneficiary bank network timed out.',
      expectedValue: 'HTTP 200 / ACK',
      actualValue: 'TIMEOUT_504',
      detectedAt: new Date().toISOString(),
    });
  } else if (isSeventh) {
    exceptions.push({
      id: `pex-${id}-recon`,
      paymentId: id,
      code: 'RECONCILIATION_MISMATCH',
      severity: 'WARNING',
      status: 'OPEN',
      description: `Clearing variance of 5% detected between internal instruction and bank debit advice.`,
      expectedValue: invoicePayableAmount,
      actualValue: paidAmount,
      detectedAt: new Date().toISOString(),
    });
  }

  return {
    id,
    paymentReference,
    bankReferenceNumber: status === 'PAID' ? `UTR${Math.floor(100000000000 + Math.random() * 900000000000)}` : undefined,
    invoiceId: inv.id,
    invoiceNumber: inv.invoiceNumber,
    vendorInvoiceNumber: inv.vendorInvoiceNumber,
    poId: po.id,
    poNumber: po.poNumber,
    grnId: inv.grnId,
    grnNumber: inv.grnNumber,
    vendorId: po.vendorId,
    vendorName: po.vendorName,
    vendorCode: `VND-${po.vendorId.toUpperCase()}`,
    companyCode: '1000',
    plantCode: (po as any).plant || po.items[0]?.plantCode || 'PLANT-1000',
    currency: inv.currency || 'INR',
    invoicePayableAmount,
    amount,
    paidAmount,
    remainingAmount,
    paymentDate,
    scheduledDate,
    dueDate,
    status,
    paymentMethod,
    paymentType,
    isOverdue,
    daysOverdue,
    source: i % 2 === 0 ? 'PORTAL' : 'SAP_F110',
    sapSyncStatus,
    sapDocumentNumber,
    reconciliationStatus,
    reconciliationDetails: isSeventh
      ? {
          externalReference: `EXT-BANK-${payNum}`,
          externalAmount: paidAmount,
          varianceAmount: remainingAmount,
          notes: 'Bank debit statement reflects partial clearing variance.',
        }
      : undefined,
    failureCode,
    failureReason,
    retryCount,
    maxRetries: 3,
    lastAttemptAt: isFifth ? new Date().toISOString() : undefined,
    beneficiaryBank: {
      bankName: 'HDFC Corporate Banking',
      maskedAccountNumber: `•••• •••• ${Math.floor(1000 + (i % 9000))}`,
      ifscCode: 'HDFC0000128',
      accountHolderName: po.vendorName,
    },
    readinessChecklist: [
      { id: '1', label: 'Authorized Purchase Order', passed: true, severity: 'BLOCKING', details: `PO #${po.poNumber}` },
      { id: '2', label: 'Warehouse GRN Evidence', passed: Boolean(inv.grnNumber), severity: 'BLOCKING', details: inv.grnNumber || 'Missing GRN' },
      { id: '3', label: '3-Way Match Verification', passed: (inv as any).status !== 'EXCEPTION' && (inv as any).status !== 'Discrepancy', severity: 'BLOCKING', details: 'Financial reconciliation' },
      { id: '4', label: 'AP Approval Granted', passed: true, severity: 'BLOCKING', details: 'Finance Director review' },
    ],
    exceptions,
    allocations: [
      {
        id: `alloc-${id}-1`,
        paymentId: id,
        invoiceId: inv.id,
        invoiceNumber: inv.invoiceNumber,
        allocatedAmount: amount,
        currency: inv.currency,
      },
    ],
    documentsCount: 1,
    commentsCount: i % 3,
    auditHistory: [
      {
        id: `paud-${id}-1`,
        paymentId: id,
        timestamp: new Date(Date.now() - 86400000 * 2).toISOString(),
        eventType: 'PAYMENT_CREATED',
        actorId: 'usr-fin-01',
        actorName: 'Sarah Jenkins',
        actorRole: 'Treasury Lead',
        details: `Payment instruction #${paymentReference} issued for invoice #${inv.invoiceNumber}.`,
        systemSource: 'ProcureAI',
      },
      ...(status === 'PAID'
        ? [
            {
              id: `paud-${id}-paid`,
              paymentId: id,
              timestamp: new Date(Date.now() - 86400000).toISOString(),
              eventType: 'PAYMENT_COMPLETED' as const,
              actorId: 'BANK_GATEWAY',
              actorName: 'RTGS Disbursement Router',
              actorRole: 'Banking Gateway',
              details: `Electronic payment disbursed. Reference: UTR-${payNum}.`,
              systemSource: 'BANK_GATEWAY' as const,
            },
            {
              id: `paud-${id}-sap`,
              paymentId: id,
              timestamp: new Date().toISOString(),
              eventType: 'SAP_SYNC_SUCCESS' as const,
              actorId: 'SAP_F110',
              actorName: 'SAP F110 Payment Run',
              actorRole: 'Integration Service',
              details: `Payment cleared in SAP financial ledger doc #${sapDocumentNumber}.`,
              systemSource: 'SAP_F110' as const,
            },
          ]
        : []),
    ],
    notes: 'Treasury electronic disbursement.',
    version: 1,
    createdAt: new Date(Date.now() - (i % 20) * 86400000).toISOString(),
    updatedAt: new Date().toISOString(),
  };
});

class MockPaymentService {
  private payments: PaymentRecord[] = [...GENERATED_PAYMENT_RECORDS];
  private idempotencyCache = new Map<string, PaymentRecord>();

  private findInternalRecord(id: string): PaymentRecord | undefined {
    return this.payments.find((p) => p.id === id || p.paymentReference === id);
  }

  syncPaymentRecord(payment: PaymentRecord): void {
    const idx = this.payments.findIndex((p) => p.id === payment.id || p.paymentReference === payment.paymentReference);
    if (idx >= 0) {
      this.payments[idx] = { ...this.payments[idx], ...payment };
    } else {
      this.payments.unshift(payment);
    }
  }

  async getPayments(
    filters: PaymentFilterState
  ): Promise<{ items: PaymentRecord[]; total: number; page: number; pageSize: number }> {
    await delay(env.mockDelayMs);
    let results = [...this.payments];

    if (filters.search) {
      const q = filters.search.toLowerCase().trim();
      results = results.filter(
        (p) =>
          p.paymentReference.toLowerCase().includes(q) ||
          p.invoiceNumber.toLowerCase().includes(q) ||
          p.vendorInvoiceNumber.toLowerCase().includes(q) ||
          p.poNumber.toLowerCase().includes(q) ||
          p.vendorName.toLowerCase().includes(q) ||
          (p.bankReferenceNumber && p.bankReferenceNumber.toLowerCase().includes(q)) ||
          (p.sapDocumentNumber && p.sapDocumentNumber.toLowerCase().includes(q))
      );
    }

    if (filters.status && filters.status !== 'ALL') {
      results = results.filter((p) => p.status === filters.status);
    }

    if (filters.paymentMethod && filters.paymentMethod !== 'ALL') {
      results = results.filter((p) => p.paymentMethod === filters.paymentMethod);
    }

    if (filters.plant && filters.plant !== 'ALL') {
      results = results.filter((p) => p.plantCode === filters.plant);
    }

    if (filters.vendor && filters.vendor !== 'ALL') {
      results = results.filter((p) => p.vendorId === filters.vendor || p.vendorCode === filters.vendor);
    }

    if (filters.isOverdue !== undefined) {
      results = results.filter((p) => p.isOverdue === filters.isOverdue);
    }

    // Sort
    const sortBy = filters.sortBy || 'paymentDate';
    const sortOrder = filters.sortOrder || 'desc';
    results.sort((a, b) => {
      let valA: string | number = a[sortBy];
      let valB: string | number = b[sortBy];
      if (sortBy === 'paymentDate' || sortBy === 'dueDate') {
        valA = new Date(valA as string).getTime();
        valB = new Date(valB as string).getTime();
      }
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    const page = filters.page || 1;
    const pageSize = filters.pageSize || 25;
    const startIndex = (page - 1) * pageSize;
    const paginatedItems = results.slice(startIndex, startIndex + pageSize);

    return {
      items: JSON.parse(JSON.stringify(paginatedItems)),
      total: results.length,
      page,
      pageSize,
    };
  }

  async getPaymentById(id: string): Promise<PaymentRecord | null> {
    await delay(env.mockDelayMs);
    const p = this.findInternalRecord(id);
    if (!p) return null;
    return JSON.parse(JSON.stringify(p));
  }

  async getPaymentMetrics(): Promise<PaymentMetricsSummary> {
    await delay(env.mockDelayMs);
    const totalPayments = this.payments.length;
    let totalDisbursedValue = 0;
    let readyForPaymentCount = 0;
    let pendingApprovalCount = 0;
    let scheduledCount = 0;
    let processingCount = 0;
    let partiallyPaidCount = 0;
    let paidCount = 0;
    let failedCount = 0;
    let overdueCount = 0;
    let onHoldCount = 0;
    let reconciliationRequiredCount = 0;
    let outstandingPayablesValue = 0;
    let overduePayablesValue = 0;

    this.payments.forEach((p) => {
      totalDisbursedValue += p.paidAmount;
      outstandingPayablesValue += p.remainingAmount;

      if (p.status === 'READY_FOR_PAYMENT') readyForPaymentCount++;
      if (p.status === 'PENDING_APPROVAL') pendingApprovalCount++;
      if (p.status === 'SCHEDULED') scheduledCount++;
      if (p.status === 'PROCESSING') processingCount++;
      if (p.status === 'PARTIALLY_PAID') partiallyPaidCount++;
      if (p.status === 'PAID') paidCount++;
      if (p.status === 'FAILED') failedCount++;
      if (p.status === 'ON_HOLD') onHoldCount++;
      if (p.reconciliationStatus === 'RECONCILIATION_REQUIRED' || p.reconciliationStatus === 'MISMATCH') {
        reconciliationRequiredCount++;
      }

      if (p.isOverdue) {
        overdueCount++;
        overduePayablesValue += p.remainingAmount;
      }
    });

    return {
      totalPayments,
      totalDisbursedValue: roundCurrency(totalDisbursedValue),
      readyForPaymentCount,
      pendingApprovalCount,
      scheduledCount,
      processingCount,
      partiallyPaidCount,
      paidCount,
      failedCount,
      overdueCount,
      onHoldCount,
      reconciliationRequiredCount,
      outstandingPayablesValue: roundCurrency(outstandingPayablesValue),
      overduePayablesValue: roundCurrency(overduePayablesValue),
    };
  }

  async createPaymentInstruction(
    input: CreatePaymentInput,
    idempotencyKey?: string
  ): Promise<PaymentRecord> {
    await delay(env.mockDelayMs);

    if (idempotencyKey && this.idempotencyCache.has(idempotencyKey)) {
      return JSON.parse(JSON.stringify(this.idempotencyCache.get(idempotencyKey)!));
    }

    let inv: any = MOCK_INVOICES.find((i) => i.id === input.invoiceId || i.invoiceNumber === input.invoiceId);
    if (!inv || input.invoiceId.startsWith('inv-dyn-')) {
      const dynamicInv = await mockInvoiceService.getInvoiceById(input.invoiceId);
      if (dynamicInv) inv = dynamicInv;
    }
    if (!inv) throw new Error('Invoice not found.');

    let po: any = MOCK_PURCHASE_ORDERS.find((p) => p.id === inv.poId || p.poNumber === inv.poNumber);
    if (!po || (inv.poId && inv.poId.startsWith('po-dyn-'))) {
      const dynamicPo = await mockPurchaseOrderService.getPurchaseOrderById(inv.poId);
      if (dynamicPo) po = dynamicPo;
    }
    if (!po) po = MOCK_PURCHASE_ORDERS[0]!;

    // Find existing disbursements on this invoice
    const existingOnInvoice = this.payments.filter((p) => p.invoiceId === inv.id && p.status !== 'CANCELLED');
    const alreadyPaid = existingOnInvoice.reduce((sum, p) => sum + (p.status === 'PAID' ? p.amount : 0), 0);

    // Readiness & Overpayment Validation
    const eligibility = evaluatePaymentEligibility({
      invoice: inv as any,
      hasBankMasterData: true,
      proposedAmount: input.amount,
      alreadyPaidAmount: alreadyPaid,
    });

    if (!eligibility.isEligible) {
      throw new Error(`Payment blocked: ${eligibility.blockingReasons.join('; ')}`);
    }

    const valResult = validatePaymentAmount(input.amount, inv.totalAmount - alreadyPaid);
    if (!valResult.isValid) {
      throw new Error(valResult.errorMessage || 'Invalid payment amount.');
    }

    // Duplicate Check
    const dupResult = detectDuplicatePayment(
      {
        invoiceId: inv.id,
        amount: input.amount,
      },
      this.payments
    );
    if (dupResult.isDuplicate) {
      throw new Error(dupResult.reason || 'Duplicate payment instruction flagged.');
    }

    const nextPayNum = 8001 + this.payments.length;
    const nextId = `pay-${nextPayNum}`;
    const nextRef = `PAY-${nextPayNum}`;

    const remainingBal = calculateRemainingBalance(inv.totalAmount, alreadyPaid, input.amount);
    const pType = input.paymentType || determinePaymentType(input.amount, inv.totalAmount - alreadyPaid);

    const newPayment: PaymentRecord = {
      id: nextId,
      paymentReference: nextRef,
      invoiceId: inv.id,
      invoiceNumber: inv.invoiceNumber,
      vendorInvoiceNumber: inv.vendorInvoiceNumber,
      poId: po.id,
      poNumber: po.poNumber,
      grnId: inv.grnId,
      grnNumber: inv.grnNumber,
      vendorId: po.vendorId,
      vendorName: po.vendorName,
      vendorCode: `VND-${po.vendorId.toUpperCase()}`,
      companyCode: '1000',
      plantCode: (po as any).plant || po.items[0]?.plantCode || 'PLANT-1000',
      currency: inv.currency,
      invoicePayableAmount: inv.totalAmount,
      amount: input.amount,
      paidAmount: remainingBal.newPaidAmount,
      remainingAmount: remainingBal.remainingAmount,
      paymentDate: new Date().toISOString().slice(0, 10),
      scheduledDate: input.scheduledDate || new Date().toISOString().slice(0, 10),
      dueDate: inv.dueDate,
      status: 'SCHEDULED',
      paymentMethod: input.paymentMethod,
      paymentType: pType,
      isOverdue: false,
      daysOverdue: 0,
      source: 'PORTAL',
      sapSyncStatus: 'NOT_SYNCED',
      reconciliationStatus: 'MISSING_EXTERNAL_RECORD',
      retryCount: 0,
      maxRetries: 3,
      beneficiaryBank: {
        bankName: 'HDFC Corporate Banking',
        maskedAccountNumber: '•••• •••• 4582',
        ifscCode: 'HDFC0000128',
        accountHolderName: po.vendorName,
      },
      readinessChecklist: eligibility.checklist,
      exceptions: [],
      allocations: [
        {
          id: `alloc-${nextId}-1`,
          paymentId: nextId,
          invoiceId: inv.id,
          invoiceNumber: inv.invoiceNumber,
          allocatedAmount: input.amount,
          currency: inv.currency,
        },
      ],
      documentsCount: 0,
      commentsCount: 0,
      auditHistory: [
        {
          id: `paud-${nextId}-created`,
          paymentId: nextId,
          timestamp: new Date().toISOString(),
          eventType: 'PAYMENT_CREATED',
          actorId: 'usr-fin-01',
          actorName: 'Sarah Jenkins',
          actorRole: 'Treasury Lead',
          details: `Payment instruction #${nextRef} created for ${input.amount} ${inv.currency}.`,
          systemSource: 'ProcureAI',
        },
      ],
      notes: input.notes || 'Treasury disbursement instruction.',
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.payments.unshift(newPayment);
    if (idempotencyKey) {
      this.idempotencyCache.set(idempotencyKey, newPayment);
    }

    notifyGlobal('Payment Instruction Created', `Payment ${newPayment.paymentReference} scheduled for ${newPayment.vendorName}.`, 'System');

    return JSON.parse(JSON.stringify(newPayment));
  }

  async executePayment(id: string, _idempotencyKey?: string): Promise<PaymentRecord> {
    await delay(env.mockDelayMs);
    const p = this.findInternalRecord(id);
    if (!p) throw new Error('Payment not found');

    if (p.status === 'PAID') {
      return JSON.parse(JSON.stringify(p));
    }

    p.paidAmount = roundCurrency(p.paidAmount + p.amount);
    p.remainingAmount = Math.max(0, roundCurrency(p.invoicePayableAmount - p.paidAmount));
    p.status = p.remainingAmount <= 0.01 ? 'PAID' : 'PARTIALLY_PAID';
    p.bankReferenceNumber = `UTR${Math.floor(100000000000 + Math.random() * 900000000000)}`;
    p.reconciliationStatus = 'MATCHED';
    p.sapSyncStatus = 'SYNCED';
    p.sapDocumentNumber = `150000${p.paymentReference.replace(/\D/g, '')}`;
    p.version += 1;
    p.updatedAt = new Date().toISOString();

    p.auditHistory.unshift({
      id: `paud-${p.id}-${Date.now()}`,
      paymentId: p.id,
      timestamp: p.updatedAt,
      eventType: 'PAYMENT_COMPLETED',
      actorId: 'BANK_GATEWAY',
      actorName: 'Banking Clearing Network',
      actorRole: 'Payment Gateway',
      details: `Disbursement completed via ${p.paymentMethod}. Bank clearing reference: ${p.bankReferenceNumber}.`,
      systemSource: 'BANK_GATEWAY',
    });

    notifyGlobal('Payment Disbursed', `Payment ${p.paymentReference} executed successfully (${p.bankReferenceNumber}).`, 'System');

    return JSON.parse(JSON.stringify(p));
  }

  async retryPayment(id: string): Promise<PaymentRecord> {
    await delay(env.mockDelayMs);
    const p = this.findInternalRecord(id);
    if (!p) throw new Error('Payment not found');

    if (p.retryCount >= p.maxRetries) {
      throw new Error(`Maximum retry attempts (${p.maxRetries}) exceeded. Manual intervention required.`);
    }

    p.retryCount += 1;
    p.status = 'PROCESSING';
    p.lastAttemptAt = new Date().toISOString();
    p.version += 1;
    p.updatedAt = new Date().toISOString();

    p.auditHistory.unshift({
      id: `paud-${p.id}-${Date.now()}`,
      paymentId: p.id,
      timestamp: p.updatedAt,
      eventType: 'PAYMENT_RETRY_REQUESTED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Treasury Lead',
      details: `Payment retry attempt ${p.retryCount}/${p.maxRetries} submitted.`,
      systemSource: 'ProcureAI',
    });

    // Simulate successful retry outcome
    return this.executePayment(p.id);
  }

  async reconcilePayment(id: string, resolutionNotes: string): Promise<PaymentRecord> {
    await delay(env.mockDelayMs);
    const p = this.findInternalRecord(id);
    if (!p) throw new Error('Payment not found');

    p.reconciliationStatus = 'MATCHED';
    if (p.status === 'RECONCILIATION_REQUIRED') {
      p.status = 'PAID';
    }
    p.version += 1;
    p.updatedAt = new Date().toISOString();

    p.auditHistory.unshift({
      id: `paud-${p.id}-${Date.now()}`,
      paymentId: p.id,
      timestamp: p.updatedAt,
      eventType: 'PAYMENT_RECONCILIATION_COMPLETED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Treasury Lead',
      details: `Bank reconciliation confirmed. Notes: ${resolutionNotes}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal('Payment Reconciled', `Payment ${p.paymentReference} reconciled with bank statement.`, 'System');

    return JSON.parse(JSON.stringify(p));
  }

  async approvePayment(id: string, notes?: string): Promise<PaymentRecord> {
    await delay(env.mockDelayMs);
    const p = this.findInternalRecord(id);
    if (!p) throw new Error('Payment not found');

    p.status = 'APPROVED';
    p.version += 1;
    p.updatedAt = new Date().toISOString();

    p.auditHistory.unshift({
      id: `paud-${p.id}-${Date.now()}`,
      paymentId: p.id,
      timestamp: p.updatedAt,
      eventType: 'PAYMENT_APPROVED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Finance Director',
      details: `Payment authorization approved. ${notes || ''}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal('Payment Approved', `Payment ${p.paymentReference} approved for release.`, 'System');

    return JSON.parse(JSON.stringify(p));
  }

  async placeOnHold(id: string, reason: string): Promise<PaymentRecord> {
    await delay(env.mockDelayMs);
    const p = this.findInternalRecord(id);
    if (!p) throw new Error('Payment not found');

    p.status = 'ON_HOLD';
    p.version += 1;
    p.updatedAt = new Date().toISOString();

    p.auditHistory.unshift({
      id: `paud-${p.id}-${Date.now()}`,
      paymentId: p.id,
      timestamp: p.updatedAt,
      eventType: 'PAYMENT_HOLD_PLACED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Treasury Lead',
      details: `Payment held. Reason: ${reason}`,
      systemSource: 'ProcureAI',
    });

    return JSON.parse(JSON.stringify(p));
  }

  async releaseHold(id: string): Promise<PaymentRecord> {
    await delay(env.mockDelayMs);
    const p = this.findInternalRecord(id);
    if (!p) throw new Error('Payment not found');

    p.status = 'READY_FOR_PAYMENT';
    p.version += 1;
    p.updatedAt = new Date().toISOString();

    p.auditHistory.unshift({
      id: `paud-${p.id}-${Date.now()}`,
      paymentId: p.id,
      timestamp: p.updatedAt,
      eventType: 'PAYMENT_HOLD_RELEASED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Treasury Lead',
      details: 'Payment hold released following review.',
      systemSource: 'ProcureAI',
    });

    return JSON.parse(JSON.stringify(p));
  }

  async cancelPayment(id: string, reason?: string): Promise<PaymentRecord> {
    await delay(env.mockDelayMs);
    const p = this.findInternalRecord(id);
    if (!p) throw new Error('Payment not found');

    if (p.status === 'PAID') {
      throw new Error('Completed payments cannot be cancelled.');
    }

    p.status = 'CANCELLED';
    p.version += 1;
    p.updatedAt = new Date().toISOString();

    p.auditHistory.unshift({
      id: `paud-${p.id}-${Date.now()}`,
      paymentId: p.id,
      timestamp: p.updatedAt,
      eventType: 'PAYMENT_CANCELLED',
      actorId: 'usr-fin-01',
      actorName: 'Sarah Jenkins',
      actorRole: 'Treasury Lead',
      details: `Payment cancelled. Reason: ${reason || 'Administrative cancellation'}`,
      systemSource: 'ProcureAI',
    });

    return JSON.parse(JSON.stringify(p));
  }

  async syncWithSapF110(id: string): Promise<{ success: boolean; sapDocumentNumber: string; payment: PaymentRecord }> {
    await delay(env.mockDelayMs);
    const p = this.findInternalRecord(id);
    if (!p) throw new Error('Payment not found');

    p.sapSyncStatus = 'SYNCED';
    p.sapDocumentNumber = p.sapDocumentNumber || `150000${p.paymentReference.replace(/\D/g, '')}`;
    p.version += 1;
    p.updatedAt = new Date().toISOString();

    p.auditHistory.unshift({
      id: `paud-${p.id}-${Date.now()}`,
      paymentId: p.id,
      timestamp: p.updatedAt,
      eventType: 'SAP_SYNC_SUCCESS',
      actorId: 'SAP_F110',
      actorName: 'SAP F110 Payment Run',
      actorRole: 'Integration Service',
      details: `Posted to SAP S/4HANA financial clearing document #${p.sapDocumentNumber}.`,
      systemSource: 'SAP_F110',
    });

    notifyGlobal('SAP F110 Synced', `Payment ${p.paymentReference} posted to SAP F110 (${p.sapDocumentNumber}).`, 'System');

    return {
      success: true,
      sapDocumentNumber: p.sapDocumentNumber,
      payment: JSON.parse(JSON.stringify(p)),
    };
  }

  async updatePayment(id: string, data: Partial<PaymentRecord>): Promise<PaymentRecord> {
    await delay(env.mockDelayMs);
    const p = this.findInternalRecord(id);
    if (!p) throw new Error('Payment record not found');
    Object.assign(p, data);
    p.version += 1;
    p.updatedAt = new Date().toISOString();
    return JSON.parse(JSON.stringify(p));
  }

  async deletePayment(id: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    const initialLen = this.payments.length;
    this.payments = this.payments.filter((p) => p.id !== id && p.paymentReference !== id);
    return this.payments.length < initialLen;
  }
}

export const mockPaymentService = new MockPaymentService();
